import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { toast } from "sonner";
import { Loader2, Send, Building2, ShieldCheck, TrendingUp, HeadphonesIcon } from "lucide-react";

/**
 * Gap 1 — Prospects landing page.
 * Public form → POST /api/classifieds/vendor-apply
 * Applications feed Admin → Prospects as `draft` "Self-applied (website)".
 * Includes bot honeypot `company` field (must stay empty).
 */
export default function ListYourBusinessPage() {
  const [f, setF] = useState({
    business_name: "",
    contact_name: "",
    contact_email: "",
    phone: "",
    website_url: "",
    category: "",
    message: "",
    company: "", // HONEYPOT — must stay empty
  });
  const [submitted, setSubmitted] = useState(null);

  const mut = useMutation({
    mutationFn: async () => {
      const { data } = await sharedApi.post("/classifieds/vendor-apply", {
        business_name: f.business_name.trim(),
        contact_name: f.contact_name.trim() || undefined,
        contact_email: f.contact_email.trim(),
        phone: f.phone.trim() || undefined,
        website_url: f.website_url.trim() || undefined,
        category: f.category.trim() || undefined,
        message: f.message.trim() || undefined,
        company: f.company, // honeypot
      });
      return data;
    },
    onSuccess: (data) => {
      setSubmitted(data);
      setF({
        business_name: "",
        contact_name: "",
        contact_email: "",
        phone: "",
        website_url: "",
        category: "",
        message: "",
        company: "",
      });
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Couldn't send — please try again."),
  });

  const submit = (e) => {
    e.preventDefault();
    if (!f.business_name.trim() || !f.contact_email.trim()) {
      toast.error("Business name and email are required.");
      return;
    }
    if (f.business_name.trim().length < 2 || f.business_name.trim().length > 120) {
      toast.error("Business name should be between 2 and 120 characters.");
      return;
    }
    mut.mutate();
  };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <Seo
        title="List your business on Allsale"
        description="Grow your Kiwi business with Allsale Classified. Tell us what you sell and our team will set you up."
      />
      <Breadcrumbs items={[{ label: "List your business" }]} />

      <div className="mt-6 grid md:grid-cols-5 gap-8">
        {/* Left: value props */}
        <div className="md:col-span-2 space-y-6">
          <div className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-orange)]/10 text-[color:var(--brand-orange)] px-3 py-1 text-xs font-bold uppercase tracking-widest">
            <Building2 className="w-3.5 h-3.5" /> For dealers &amp; businesses
          </div>
          <h1 className="font-display text-3xl md:text-5xl font-black text-slate-900 leading-tight">
            List your business on Allsale
          </h1>
          <p className="text-slate-600 leading-relaxed">
            Tell us what you sell and our team will set you up — bulk-list your stock,
            get a branded seller page, and reach thousands of Kiwis every day.
          </p>

          <ul className="space-y-4 text-sm">
            <li className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] grid place-items-center shrink-0">
                <TrendingUp className="w-4.5 h-4.5" />
              </div>
              <div>
                <div className="font-bold text-slate-900">Reach thousands of Kiwi buyers</div>
                <div className="text-slate-500">Featured placement, region targeting and category exposure across NZ.</div>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] grid place-items-center shrink-0">
                <ShieldCheck className="w-4.5 h-4.5" />
              </div>
              <div>
                <div className="font-bold text-slate-900">Verified &amp; trusted</div>
                <div className="text-slate-500">Branded seller page with your logo, licence and WhatsApp — buyers know you&apos;re legit.</div>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] grid place-items-center shrink-0">
                <HeadphonesIcon className="w-4.5 h-4.5" />
              </div>
              <div>
                <div className="font-bold text-slate-900">Hands-on onboarding</div>
                <div className="text-slate-500">Our team helps you upload stock and get your first sales — no DIY.</div>
              </div>
            </li>
          </ul>
        </div>

        {/* Right: form (or success) */}
        <div className="md:col-span-3">
          {submitted ? (
            <div
              data-testid="vendor-apply-success"
              className="rounded-2xl bg-white shadow-card p-8 md:p-10 border border-emerald-100"
            >
              <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 grid place-items-center">
                <Send className="w-5 h-5" />
              </div>
              <h2 className="mt-4 font-display text-2xl font-black text-slate-900">
                {submitted.already_pending ? "You're already on our list" : "Thanks — application received!"}
              </h2>
              <p className="mt-2 text-slate-600 leading-relaxed">
                {submitted.message || "Our team will review your application and be in touch within one business day."}
              </p>
              <button
                type="button"
                onClick={() => setSubmitted(null)}
                data-testid="vendor-apply-again"
                className="mt-6 text-sm font-semibold text-[color:var(--brand-blue)] hover:underline"
              >
                Submit another business
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="vendor-apply-form"
              className="rounded-2xl bg-white shadow-card p-6 md:p-8 space-y-4"
            >
              {/* Honeypot — visually hidden, must remain empty */}
              <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", height: 0, overflow: "hidden" }}>
                <label>
                  Leave this blank
                  <input
                    tabIndex={-1}
                    autoComplete="off"
                    value={f.company}
                    onChange={(e) => setF({ ...f, company: e.target.value })}
                  />
                </label>
              </div>

              <Field label="Business name *">
                <input
                  data-testid="vendor-business-name"
                  value={f.business_name}
                  onChange={(e) => setF({ ...f, business_name: e.target.value })}
                  required
                  minLength={2}
                  maxLength={120}
                  className="input"
                  placeholder="Hamilton Motors"
                />
              </Field>

              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Your name">
                  <input
                    data-testid="vendor-contact-name"
                    value={f.contact_name}
                    onChange={(e) => setF({ ...f, contact_name: e.target.value })}
                    className="input"
                    placeholder="Raj"
                  />
                </Field>
                <Field label="Email *">
                  <input
                    data-testid="vendor-contact-email"
                    type="email"
                    value={f.contact_email}
                    onChange={(e) => setF({ ...f, contact_email: e.target.value })}
                    required
                    className="input"
                    placeholder="sales@yourbusiness.co.nz"
                  />
                </Field>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Phone (optional)">
                  <input
                    data-testid="vendor-phone"
                    value={f.phone}
                    onChange={(e) => setF({ ...f, phone: e.target.value })}
                    className="input"
                    placeholder="021 555 0000"
                  />
                </Field>
                <Field label="Website (optional)">
                  <input
                    data-testid="vendor-website"
                    value={f.website_url}
                    onChange={(e) => setF({ ...f, website_url: e.target.value })}
                    className="input"
                    placeholder="https://yourbusiness.co.nz"
                  />
                </Field>
              </div>

              <Field label="What do you sell? (category)">
                <input
                  data-testid="vendor-category"
                  value={f.category}
                  onChange={(e) => setF({ ...f, category: e.target.value })}
                  className="input"
                  placeholder="Cars, Property, Electronics…"
                />
              </Field>

              <Field label="Tell us more (optional)">
                <textarea
                  data-testid="vendor-message"
                  value={f.message}
                  onChange={(e) => setF({ ...f, message: e.target.value })}
                  maxLength={1500}
                  className="input min-h-32"
                  placeholder="How many listings? Any special requirements?"
                />
              </Field>

              <button
                type="submit"
                data-testid="vendor-apply-submit"
                disabled={mut.isPending}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold py-3 disabled:opacity-70 transition-colors"
              >
                {mut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send application
              </button>

              <p className="text-xs text-slate-400 text-center">
                We usually reply within one business day.
              </p>
            </form>
          )}
        </div>
      </div>

      <style>{`.input{width:100%;background:#fff;border:1px solid var(--border-soft);border-radius:14px;padding:.7rem 1rem;font-size:.95rem;color:var(--text-primary);}
      .input:focus{outline:none;border-color:var(--brand-blue);box-shadow:0 0 0 3px rgba(28,169,224,.12);}
      textarea.input{padding-top:.75rem;padding-bottom:.75rem;line-height:1.5;}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
